// /templates — Session 7 PR3 message-draft template overrides.
//
// One card per message kind. Each shows the active system prompt (override
// or default), with edit-in-place save + revert-to-default. Overrides take
// effect on the next /messages/draft call without redeploy.

import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Textarea } from "../components/ui/Textarea";
import { Pill } from "../components/ui/Pill";
import { Skeleton } from "../components/ui/Skeleton";
import { EmptyState } from "../components/ui/EmptyState";
import {
  useDeleteTemplate,
  usePutTemplate,
  useTemplates,
  type TemplateRow,
} from "../lib/templates";

export default function Templates() {
  const q = useTemplates();

  return (
    <section className="space-y-4 p-4 sm:p-6 lg:p-8">
      <header>
        <h1 className="text-xl font-semibold text-neutral-100">Message templates</h1>
        <p className="mt-1 text-xs text-neutral-500">
          System prompts that drive the message-draft composer for each kind.
          Overrides apply on the next draft generation. Revert any time.
        </p>
      </header>

      {q.isLoading ? (
        <ul className="space-y-3">
          {[0, 1].map((i) => (
            <li key={i}>
              <Skeleton className="h-48 w-full rounded-lg" />
            </li>
          ))}
        </ul>
      ) : q.error ? (
        <EmptyState
          title="Couldn't load templates"
          description={q.error instanceof Error ? q.error.message : "Try again."}
        />
      ) : (
        <ul className="space-y-3">
          {(q.data?.templates ?? []).map((row) => (
            <li key={row.kind}>
              <TemplateCard row={row} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TemplateCard({ row }: { row: TemplateRow }) {
  const put = usePutTemplate(row.kind);
  const del = useDeleteTemplate(row.kind);
  const [draft, setDraft] = useState(row.active_system_prompt);

  // Sync local draft back when the server-side data changes (after save or
  // reset). Avoids stale local state shadowing the canonical value.
  useEffect(() => {
    setDraft(row.active_system_prompt);
  }, [row.active_system_prompt]);

  const dirty = draft !== row.active_system_prompt;
  const matchesDefault = draft === row.default_system_prompt;

  return (
    <Card>
      <CardHeader
        title={row.label}
        description={
          row.is_overridden && row.updated_at
            ? `Overridden — last saved ${new Date(row.updated_at).toLocaleString()}`
            : "Using bundled default"
        }
        action={
          <Pill tone={row.is_overridden ? "info" : "neutral"}>
            {row.is_overridden ? "Custom" : "Default"}
          </Pill>
        }
      />
      <CardBody className="space-y-3">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={12}
          className="font-mono text-xs"
        />
        <div className="flex items-center justify-end gap-2">
          {row.is_overridden ? (
            <Button
              variant="ghost"
              leadingIcon={<RotateCcw size={12} />}
              onClick={() => del.mutate()}
              disabled={del.isPending}
            >
              Revert to default
            </Button>
          ) : null}
          <Button
            variant="ghost"
            disabled={!dirty}
            onClick={() => setDraft(row.active_system_prompt)}
          >
            Discard edits
          </Button>
          <Button
            disabled={!dirty || put.isPending || matchesDefault}
            onClick={() => put.mutate({ system_prompt: draft })}
          >
            {put.isPending ? "Saving…" : "Save override"}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
